import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  clearSession,
  getToken,
  loadMembers,
  saveMembers,
  saveSession,
  sessionIsRestorable,
  type Member,
  type PairResult,
  type Role,
} from '../lib/api';

export type PairStatus = 'pair' | 'chat';

export interface SessionState {
  status: PairStatus;
  /**
   * The couple's 6-char code. Shown optimistically while create resolves and
   * kept afterwards — it used to live only on the pair screen, which unmounts
   * the moment the chat opens, so a creator had no way to share it with their
   * partner.
   */
  pendingCode: string | null;
  members: Member[];
  role: Role | null;
  memberId: string | null;
  busy: boolean;
}

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCode(): string {
  let out = '';
  for (let i = 0; i < 6; i++) out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  return out;
}

async function syncMembers(): Promise<Member[]> {
  try {
    const status = await api<{ couple?: { members?: Member[] } }>('/api/pair/status');
    const members = status.couple?.members ?? [];
    if (members.length) saveMembers(members);
    return members;
  } catch {
    return [];
  }
}

/**
 * Pairing + session lifecycle. Replaces `handleCreate` / `handleJoin` /
 * `leaveThread` / `init` from `main.ts`.
 *
 * `onInvalidToken` lets the shell drop back to the pair screen when the
 * WebSocket reports `bad_token` — same behaviour as before.
 */
export function useSession(onInvalidToken: () => void) {
  const [state, setState] = useState<SessionState>(() => ({
    status: 'pair',
    pendingCode: null,
    members: sessionIsRestorable() ? loadMembers() : [],
    role: null,
    memberId: null,
    busy: false,
  }));
  const invalidRef = useRef(onInvalidToken);
  invalidRef.current = onInvalidToken;

  const enterChat = useCallback((role: Role, memberId: string) => {
    setState(s => ({ ...s, status: 'chat', role, memberId, busy: false }));
  }, []);

  const create = useCallback(
    async (displayName: string) => {
      setState(s => ({ ...s, busy: true, pendingCode: randomCode() }));
      try {
        const result = await api<PairResult>('/api/pair/create', { body: { displayName } });
        saveSession(result.token, result.couple.coupleId, result.role, result.memberId);
        const members = await syncMembers();
        setState(s => ({ ...s, members, pendingCode: result.couple.code ?? s.pendingCode }));
        enterChat(result.role, result.memberId);
        return result.token;
      } catch (e) {
        console.error('[Pear] Create failed:', e);
        setState(s => ({ ...s, busy: false, pendingCode: null }));
        return null;
      }
    },
    [enterChat],
  );

  const join = useCallback(
    async (code: string, displayName: string) => {
      setState(s => ({ ...s, busy: true, pendingCode: code }));
      try {
        const result = await api<PairResult>('/api/pair/join', { body: { code, displayName } });
        saveSession(result.token, result.couple.coupleId, result.role, result.memberId);
        const members = await syncMembers();
        setState(s => ({ ...s, members }));
        enterChat(result.role, result.memberId);
        return result.token;
      } catch (e) {
        console.error('[Pear] Join failed:', e);
        setState(s => ({ ...s, busy: false }));
        return null;
      }
    },
    [enterChat],
  );

  const leave = useCallback(() => {
    clearSession();
    setState({ status: 'pair', pendingCode: null, members: [], role: null, memberId: null, busy: false });
  }, []);

  const reportInvalidToken = useCallback(() => invalidRef.current(), []);

  /** Restore a session persisted before the migration (same localStorage keys). */
  useEffect(() => {
    if (sessionIsRestorable()) {
      const role = (localStorage.getItem('pear_role') ?? 'A') as Role;
      const memberId = localStorage.getItem('pear_member_id');
      setState(s => ({ ...s, status: 'chat', role, memberId }));
    }
  }, []);

  return { ...state, create, join, leave, reportInvalidToken };
}
